/**
 * `createApiExplorerSource` — the explorer's WRITE port over the shared
 * `ApiClient`.
 *
 * The transaction explorer READS through `@authe/analytics-widgets` hooks
 * (catalog / stream / facet-values, via ui-core `useQuery`); this port is only
 * the mutations that turn browsing into durable structure: promote a browsed
 * field to an indexed **facet** (`POST /metadata-facets`), assemble facets into
 * a **view** (`POST /metadata-views`), and build authored views into physical
 * `mv_*` relations (`POST /metadata-analytics/materialize`). Bodies + the
 * `?scope=` rung are assembled by the framework-agnostic builders in
 * `@authe/fe-commons/metadata-analytics`, so the wire contract lives in exactly
 * one place. Canonical `/metadata-*` paths are rewritten to
 * `internal/metadata-registry-api` by `RESOURCE_REWRITES`.
 */
import { unwrapData, type ApiClient } from "@authe/fe-commons/api";
import {
  type CatalogField,
  configPaths,
  facetCreateBody,
  materializeRequest,
  scopeQuery,
  viewCreateBody,
} from "@authe/fe-commons/metadata-analytics";

/** Ladder rung a write authors at (mirrors the backend `FacetVisibility`). */
export type WriteScope = "customer" | "org" | "tenant" | "user";

export interface CreateViewInput {
  viewId: string;
  facetNames: string[];
  source?: string;
  protocolId?: string;
  groupBy?: string[];
  scope?: WriteScope;
}

export interface ExplorerSource {
  /** Promote a browsed field to a durable indexed facet (default rung: tenant). */
  createFacet(field: CatalogField, scope?: WriteScope): Promise<void>;
  /** Assemble facets into a materialized-view spec (default rung: tenant). */
  createView(input: CreateViewInput): Promise<void>;
  /** Build authored views into physical `mv_*` relations (one, or all in scope). */
  materialize(viewId?: string): Promise<void>;
}

export function createApiExplorerSource(client: ApiClient): ExplorerSource {
  return {
    async createFacet(field: CatalogField, scope?: WriteScope): Promise<void> {
      unwrapData(await client.post(configPaths.facets(), facetCreateBody(field, { visibility: scope ?? "tenant" }), {
        query: scopeQuery(scope),
      }));
      client.invalidate(configPaths.facets());
      client.invalidateMatching("/metadata-analytics/catalog");
    },

    async createView(input: CreateViewInput): Promise<void> {
      const body = viewCreateBody({
        viewId: input.viewId,
        facetNames: input.facetNames,
        scope: input.scope,
        source: input.source,
        protocolId: input.protocolId,
        groupBy: input.groupBy,
      });
      unwrapData(await client.post(configPaths.views(), body, { query: scopeQuery(input.scope) }));
      client.invalidate(configPaths.views());
    },

    async materialize(viewId?: string): Promise<void> {
      const req = materializeRequest(viewId);
      unwrapData(await client.post(req.path, {}, { query: req.query }));
    },
  };
}
